/**
 * Copyright (C) 2025 GIP-RECIA, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Link } from 'common/types/index.ts'
import { $settings } from '../stores/index.ts'

function getServiceLink(
  fname: string,
  alternativeMaximizedLink: string | undefined,
  alternativeMaximizedLinkTarget: string | undefined,
): Link {
  const { contextApiUrl } = $settings.get()

  return {
    href: alternativeMaximizedLink
      ? getDomainLink(alternativeMaximizedLink)
      : getDomainLink(`${contextApiUrl}/p/${fname}`),
    target: alternativeMaximizedLink
      ? (alternativeMaximizedLinkTarget ?? '_blank')
      : '_self',
    rel: alternativeMaximizedLink
      ? 'noopener noreferrer'
      : undefined,
  }
}

function getDomainLink(
  path: string,
): string {
  if (import.meta.env.DEV)
    return path

  const { domain } = $settings.get()

  if (path.startsWith('http'))
    return path

  return `https://${domain}${path.startsWith('/') ? '' : '/'}${path}`
}

function isCurrentPage(link: string): boolean {
  const { contextApiUrl } = $settings.get()
  const { origin, href, pathname } = window.location

  const currentPath = pathname.split('.')[0]
  const targetURL = new URL(link, origin)
  const targetPath = targetURL.pathname

  if (link.startsWith('#') || link === '')
    return pathname === new URL(href).pathname

  if (targetPath.startsWith(contextApiUrl ?? '/'))
    return currentPath.startsWith(targetPath)

  return pathname === targetPath
}

function getBusteredLink(path: string): string {
  const { cacheBusterVersion } = $settings.get()

  const [base, hash] = path.split('#')
  const baseClean = base.replace(/\?v=\d+/, '')
  const newBase = baseClean + (cacheBusterVersion ? `?v=${cacheBusterVersion}` : '')

  return hash ? `${newBase}#${hash}` : newBase
}

function removeProtocol(url: string): string {
  return url.replace(/^[a-z]+:\/\//i, '')
}

export {
  getBusteredLink,
  getDomainLink,
  getServiceLink,
  isCurrentPage,
  removeProtocol,
}
